import React from 'react'
import { union } from 'lodash'
import { getHours, getMinutes, set } from 'date-fns'
import {
  useGroupWithMembersData,
  AddCompanyGroupMembers_addCompanyGroupMembers,
  useTestSessionForm,
  MemberFormValues,
  TestSessionFormValues as RequestSessionFormValues,
  useRequestGroupTestSession,
  getTestSessionGqlCompliant,
  useCompanyDetails,
  TestSessionFieldName,
  TestType,
} from '@modmd/data'
import { AddressInput } from 'components/AddressInput'
import { GroupMembersSelector } from 'sharedComponents/GroupMembersSelector'
import { Box } from 'components/Layout'
import { LoadingSkeleton } from 'components/Datalist'
import Button from 'components/Button'
import { SelectInput } from 'components/SelectInput'
import { useToaster } from 'utils/useToaster'
import TextInput from 'components/TextInput'
import { DateTime } from 'components/DateTime'
import { Loading } from 'components/Loading'
import { Switch } from 'components/Switch'
import { Text } from 'components/Typography'
import { testTypesOptions } from 'client-portal/constants/testTypesOptions'
import { setTimeToDate } from 'utils/helpers'
import { Sublocation } from 'sharedComponents/Sublocation/Sublocation'

export interface RequestProps {
  reqSessionFormValues: RequestSessionFormValues
  sessionId: string
}

interface Props {
  groupId: string
  companyId: string
  minHoursBeforeBooking?: number
  onRequestSuccess: ({ reqSessionFormValues, sessionId }: RequestProps) => void
  showInsuranceOptions?: boolean
}

const RequestTestSession: React.VFC<Props> = ({
  groupId,
  companyId,
  onRequestSuccess,
  minHoursBeforeBooking,
  showInsuranceOptions = true,
}) => {
  const { setToastMessage } = useToaster()
  const [membersPage, setMembersPage] = React.useState(1)
  const groupWithMembersQuery = useGroupWithMembersData({
    groupId,
    membersPage,
    filterData: {
      isDeleted: false,
    },
  })
  const [
    requestGroupTestSession,
    { loading: isRequestGroupTestSessionLoading },
  ] = useRequestGroupTestSession()
  const { data: companyDetailsData, loading: isCompanyDetailsLoading } = useCompanyDetails({
    filter: { companyId, skipGroups: true },
  })

  const testSessionForm = useTestSessionForm({
    minHoursBeforeBooking,
    validationSchema: 'withEstimatedMembers',
    onSubmit: async (values) => {
      try {
        const response = await requestGroupTestSession({
          variables: {
            inputData: {
              endsAt: values.endDate,
              ...getTestSessionGqlCompliant(values, [
                'date',
                'location',
                'excludedMembersIds',
                'members',
                'estimatedMembers',
              ]),
              isInsurance: values.onlyInsurance,
              type: values.testType,
              groupId,
              isAllSelected: values.isAllSelected,
              gmt: new Date().getTimezoneOffset(),
              sublocation: values.sublocation,
            },
          },
        })
        if (response.data?.requestGroupTestSession) {
          const { id } = response.data.requestGroupTestSession
          setToastMessage('Test session requested', 'success')
          onRequestSuccess({
            reqSessionFormValues: values,
            sessionId: id,
          })
        }
      } catch {
        // ignore
      }
    },
  })

  const handleAddMember = React.useCallback(
    (user: MemberFormValues) => {
      if (testSessionForm.values.isAllSelected) {
        void testSessionForm.setFieldValue(
          'excludedMembersIds',
          testSessionForm.values.excludedMembersIds?.filter((id) => id !== user.User.id) || []
        )
      } else {
        void testSessionForm.setFieldValue('Members', [...testSessionForm.values.Members, user])
      }
    },
    [testSessionForm]
  )

  const handleRemoveMember = React.useCallback(
    (userId: string) => {
      if (testSessionForm.values.isAllSelected) {
        void testSessionForm.setFieldValue(
          'excludedMembersIds',
          union(testSessionForm.values.excludedMembersIds, [userId])
        )
      } else {
        void testSessionForm.setFieldValue(
          'Members',
          testSessionForm.values.Members.filter(({ User }) => User.id !== userId)
        )
      }
    },
    [testSessionForm]
  )

  const handleCsvUploadSuccess = React.useCallback(
    (users: AddCompanyGroupMembers_addCompanyGroupMembers[]) => {
      void groupWithMembersQuery.refetch()
      void testSessionForm.setFieldValue('excludedMembersIds', [])
      void testSessionForm.setFieldValue(
        'Members',
        users.map((User) => ({
          User,
          isMain: false,
          symptoms: [],
        }))
      )
    },
    [testSessionForm, groupWithMembersQuery]
  )

  React.useEffect(() => {
    const { insuranceOnly, pcrOnly } = companyDetailsData?.company || {}

    if (!testSessionForm) {
      return
    }

    if (insuranceOnly && testSessionForm.values[TestSessionFieldName.ONLYINSURANCE] !== true) {
      void testSessionForm.setFieldValue(TestSessionFieldName.ONLYINSURANCE, true)
    }
    if (pcrOnly && testSessionForm.values[TestSessionFieldName.TEST_TYPE] !== TestType.Covid19) {
      void testSessionForm.setFieldValue(TestSessionFieldName.TEST_TYPE, TestType.Covid19)
    }
  }, [companyDetailsData, testSessionForm])

  return (
    <>
      {isRequestGroupTestSessionLoading ? (
        <Loading isLoading skeletonComp={<LoadingSkeleton columns={4} rows={2} />} />
      ) : (
        <>
          <DateTime
            hasTime
            hasEndTime
            dateValue={testSessionForm.values.date || null}
            timeValue={testSessionForm.values.date || null}
            endTimeValue={testSessionForm.values.endDate || null}
            onDateChange={(date) => {
              void testSessionForm.setFieldValue('endDate', null)
              void testSessionForm.setFieldValue('date', date)
            }}
            onTimeChange={(date) => {
              const currentDate = setTimeToDate(new Date(testSessionForm.values.date || date), date)
              void testSessionForm.setFieldValue('endDate', null)
              void testSessionForm.setFieldValue('date', !date ? date : currentDate)
            }}
            onEndTimeChange={(date) => {
              if (testSessionForm.values.date) {
                void testSessionForm.setFieldValue(
                  'endDate',
                  date
                    ? set(testSessionForm.values.date, {
                        hours: getHours(date),
                        minutes: getMinutes(date),
                      })
                    : null
                )
              }
            }}
            onDateBlur={testSessionForm.handleBlur}
            onTimeBlur={testSessionForm.handleBlur}
            errorMessageDate={
              testSessionForm.touched.date && testSessionForm.errors.date
                ? testSessionForm.errors.date
                : undefined
            }
            minDate={new Date()}
          />
          <TextInput
            name="estimatedMembers"
            value={testSessionForm.values.estimatedMembers}
            label="Estimated members"
            onChange={testSessionForm.handleChange}
            onBlur={testSessionForm.handleBlur}
            errorMessage={
              testSessionForm.touched.estimatedMembers && testSessionForm.errors.estimatedMembers
                ? testSessionForm.errors.estimatedMembers
                : undefined
            }
            isFullWidth
          />
          <Sublocation
            sublocation={testSessionForm.values.sublocation || ''}
            refetch={() => {}}
            onChange={testSessionForm.handleChange}
          />
          <Box display="grid" gridAutoFlow="column" gridGap="1rem">
            <SelectInput
              name="testType"
              label="Test type"
              value={testSessionForm.values.testType}
              errorMessage={
                (testSessionForm.touched.testType && testSessionForm.errors.testType) || ''
              }
              onBlur={testSessionForm.handleBlur}
              onChange={testSessionForm.handleChange}
              options={testTypesOptions}
              disabled={companyDetailsData?.company?.pcrOnly}
            />
          </Box>
          <Box display="grid">
            <AddressInput
              onTouched={(touched) =>
                testSessionForm.setTouched({ ...testSessionForm.touched, Address: touched })
              }
              touched={testSessionForm.touched.Address}
              errors={testSessionForm.errors.Address}
              value={testSessionForm.values.Address}
              onChange={(address) => testSessionForm.setFieldValue('Address', address)}
            />
          </Box>
          {showInsuranceOptions && (
            <Box display="flex" alignItems="center" justifyContent="space-between" mt="1rem">
              <Text>Insurance covered Test Session</Text>
              <Switch
                isDisabled={companyDetailsData?.company?.insuranceOnly}
                isChecked={
                  companyDetailsData?.company?.insuranceOnly ||
                  !!testSessionForm.values.onlyInsurance
                }
                onChange={() =>
                  testSessionForm.setFieldValue(
                    'onlyInsurance',
                    !testSessionForm.values.onlyInsurance
                  )
                }
              />
            </Box>
          )}
        </>
      )}
      <GroupMembersSelector
        companyId={companyId}
        groupId={groupId}
        includedUserIds={testSessionForm.values.Members?.map(({ User }) => User.id!) || []}
        excludedUserIds={testSessionForm.values.excludedMembersIds || []}
        page={membersPage}
        isSelectedAll={!!testSessionForm.values.isAllSelected}
        onCsvUploadSuccess={handleCsvUploadSuccess}
        onAddMember={handleAddMember}
        onRemoveMember={handleRemoveMember}
        onPageChange={setMembersPage}
        onToggleAll={(isSelectedAll) => {
          void testSessionForm.setFieldValue('isAllSelected', isSelectedAll)
          void testSessionForm.setFieldValue(isSelectedAll ? 'excludedMembersIds' : 'Members', [])
        }}
      />

      <Button
        isFetching={isRequestGroupTestSessionLoading || isCompanyDetailsLoading}
        type="submit"
        onClick={() => testSessionForm.handleSubmit()}
      >
        Submit
      </Button>
    </>
  )
}

export { RequestTestSession }
